import React, { useState, useRef, useEffect } from 'react';
import { useUniAI } from '../hooks/useUniAI';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Mic, Square, Send, Volume2, VolumeX, Sparkles, Trash2, Loader2, Bot, User, CheckCircle2, Compass, BarChart3, ListChecks, Radio } from 'lucide-react';

const EXAMPLES = [
  { icon: ListChecks, text: 'Create a high-priority task "Homepage banner" for Melinda due Friday' },
  { icon: Sparkles, text: 'What tasks are overdue right now?' },
  { icon: BarChart3, text: 'Who is overloaded in my team?' },
  { icon: Compass, text: 'Open the Approvals page' },
];

export const UniAIPage = () => {
  const { messages, recording, processing, speaking, muted, setMuted, conversationMode, toggleConversationMode, toggleRecording, sendMessage, clearChat, stopSpeaking } = useUniAI();
  const [text, setText] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, processing]);

  const handleSend = (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    sendMessage(text.trim());
    setText('');
  };

  return (
    <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-8rem)]" data-testid="uni-ai-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="relative h-11 w-11 rounded-2xl bg-gradient-to-br from-[#F5A623] to-[#D48C1B] flex items-center justify-center shadow-lg shadow-[#F5A623]/30">
            <Sparkles className="h-5 w-5 text-black" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">Uni AI</h1>
            <p className="text-sm text-muted-foreground">Your voice copilot — create tasks, get insights, and navigate hands-free</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={conversationMode ? 'default' : 'outline'}
            size="sm"
            onClick={toggleConversationMode}
            className={`gap-1.5 ${conversationMode ? 'bg-[#F5A623] hover:bg-[#D48C1B] text-black' : ''}`}
            data-testid="uni-ai-convo-btn"
            title="Hands-free conversation — Uni AI keeps listening for your follow-ups"
          >
            <Radio className={`h-4 w-4 ${conversationMode ? 'animate-pulse' : ''}`} /> {conversationMode ? 'Hands-free On' : 'Hands-free'}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setMuted(!muted)} title={muted ? 'Unmute voice' : 'Mute voice'} data-testid="uni-ai-mute-btn">
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5 text-[#F5A623]" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={clearChat} title="Clear conversation" data-testid="uni-ai-clear-btn">
            <Trash2 className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Chat area */}
      <Card className="flex-1 overflow-hidden border-border/60 flex flex-col bg-card/60">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="uni-ai-messages">
          {messages.length === 0 && !processing && (
            <div className="h-full flex flex-col items-center justify-center text-center py-10">
              <div className="h-16 w-16 rounded-3xl bg-[#F5A623]/10 flex items-center justify-center mb-4">
                <Bot className="h-8 w-8 text-[#F5A623]" />
              </div>
              <h2 className="text-lg font-semibold mb-1">Hi, I'm Uni AI</h2>
              <p className="text-sm text-muted-foreground max-w-md mb-6">Tap the mic and speak, or type below. Try one of these:</p>
              <div className="grid sm:grid-cols-2 gap-2 w-full max-w-xl">
                {EXAMPLES.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(ex.text)}
                    className="flex items-start gap-2 text-left p-3 rounded-xl border border-border/60 hover:border-[#F5A623]/50 hover:bg-[#F5A623]/5 transition-all text-sm"
                    data-testid={`uni-ai-example-${i}`}
                  >
                    <ex.icon className="h-4 w-4 text-[#F5A623] mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{ex.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`} data-testid={`uni-ai-msg-${m.role}`}>
              <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === 'user' ? 'bg-blue-500/15 text-blue-500' : 'bg-[#F5A623]/15 text-[#F5A623]'}`}>
                {m.role === 'user' ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              </div>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'bg-blue-500/10 rounded-tr-sm' : 'bg-accent rounded-tl-sm'}`}>
                <p className="whitespace-pre-wrap">{m.content}</p>
                {m.created_task && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-green-600 font-medium">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Created: {m.created_task.task_name}
                  </div>
                )}
                {m.intent === 'navigate' && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-[#F5A623] font-medium">
                    <Compass className="h-3.5 w-3.5" /> Navigating…
                  </div>
                )}
              </div>
            </div>
          ))}

          {processing && (
            <div className="flex gap-3" data-testid="uni-ai-thinking">
              <div className="h-8 w-8 rounded-full bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="bg-accent rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-[#F5A623] animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 rounded-full bg-[#F5A623] animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="h-2 w-2 rounded-full bg-[#F5A623] animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-border/60 p-3 bg-background/50">
          <form onSubmit={handleSend} className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleRecording}
              disabled={processing}
              className={`relative h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                recording ? 'bg-red-500 text-white scale-110' : 'bg-[#F5A623] text-black hover:bg-[#D48C1B]'
              } disabled:opacity-50`}
              data-testid="uni-ai-mic-btn"
              title={recording ? 'Stop & send' : 'Start recording'}
            >
              {recording && <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-40" />}
              {recording ? <Square className="h-5 w-5 relative" /> : <Mic className="h-5 w-5 relative" />}
            </button>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={recording ? 'Listening…' : 'Type or speak your request…'}
              disabled={recording}
              className="flex-1"
              data-testid="uni-ai-text-input"
            />
            {speaking && (
              <Button type="button" variant="ghost" size="icon" onClick={stopSpeaking} title="Stop voice" data-testid="uni-ai-stop-voice-btn">
                <Volume2 className="h-5 w-5 text-[#F5A623] animate-pulse" />
              </Button>
            )}
            <Button type="submit" size="icon" disabled={processing || !text.trim()} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black flex-shrink-0" data-testid="uni-ai-send-btn">
              {processing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </Button>
          </form>
          <p className="text-[11px] text-muted-foreground mt-2 text-center">Uni AI respects your role & department access. Powered by OpenAI via your Emergent key.</p>
        </div>
      </Card>
    </div>
  );
};

export default UniAIPage;
